import { supabase } from '../utils/supabaseClient';
import { logger } from '../utils/logger';
import { 
  asRecord, 
  extractString, 
  extractDate,
} from '../utils/serviceUtils';
import type { MyProfileOverview } from '../types/analytics';

const MY_PROFILE_CACHE_KEY = 'dice1k_my_profile_cache';
let inMemoryMyProfile: MyProfileOverview | null = null;
const cacheListeners = new Set<(profile: MyProfileOverview | null) => void>();

function parseMyProfile(value: unknown): MyProfileOverview | null {
  const payload = asRecord(value);
  if (!payload) return null;

  return {
    id: String(payload.id || ''),
    displayName: extractString(payload, 'displayName', 'display_name'),
    avatarUrl: extractString(payload, 'avatarUrl', 'avatar_url'),
    friendCode: extractString(payload, 'friendCode', 'friend_code'),
    createdAt: extractDate(payload, 'createdAt', 'created_at'),
    updatedAt: extractDate(payload, 'updatedAt', 'updated_at'),
  };
}

function parseMyProfileRpcResult(value: unknown): MyProfileOverview | null {
  if (Array.isArray(value)) {
    for (const item of value) {
      const parsed = parseMyProfile(item);
      if (parsed) return parsed;
    }
    return null;
  }

  return parseMyProfile(value);
}

function notifyCacheListeners(profile: MyProfileOverview | null): void {
  cacheListeners.forEach((listener) => {
    try {
      listener(profile);
    } catch {
      // ignore listener failures
    }
  });
}

function persistCachedMyProfile(profile: MyProfileOverview | null): void {
  inMemoryMyProfile = profile;

  if (typeof localStorage !== 'undefined') {
    try {
      if (!profile?.id) {
        localStorage.removeItem(MY_PROFILE_CACHE_KEY);
      } else {
        localStorage.setItem(MY_PROFILE_CACHE_KEY, JSON.stringify(profile));
      }
    } catch {
      // ignore cache failures
    }
  }

  notifyCacheListeners(profile);
}

export function loadCachedMyProfile(userId?: string | null): MyProfileOverview | null {
  if (!userId) return null;

  if (inMemoryMyProfile?.id === userId) {
    return inMemoryMyProfile;
  }

  if (typeof localStorage === 'undefined') return null;

  try {
    const raw = localStorage.getItem(MY_PROFILE_CACHE_KEY);
    if (!raw) return null;
    const profile = parseMyProfile(JSON.parse(raw));
    if (!profile || profile.id !== userId) return null;
    inMemoryMyProfile = profile;
    return profile;
  } catch {
    return null;
  }
}

export function clearCachedMyProfile(): void {
  persistCachedMyProfile(null);
}

export function subscribeMyProfileCache(listener: (profile: MyProfileOverview | null) => void): () => void {
  cacheListeners.add(listener);
  return () => {
    cacheListeners.delete(listener);
  };
}

async function getAuthenticatedUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();

  if (error) {
    logger.error('AUTH_GET_USER_ERROR', {
      message: error.message,
    });
    throw new Error(error.message);
  }

  const userId = data.user?.id?.trim();
  if (!userId) {
    throw new Error('Missing authenticated user');
  }

  return userId;
}

async function ensureMyProfile(): Promise<MyProfileOverview | null> {
  const { data, error } = await supabase.rpc('ensure_my_profile');

  if (error) {
    logger.error('MY_PROFILE_ENSURE_ERROR', {
      message: error.message,
    });
    throw new Error(error.message);
  }

  return parseMyProfileRpcResult(data);
}

export async function fetchMyProfile(): Promise<MyProfileOverview | null> {
  const profile = await ensureMyProfile();
  persistCachedMyProfile(profile);
  return profile;
}

export async function updateMyProfileDisplayName(displayName: string): Promise<MyProfileOverview | null> {
  const trimmed = displayName.trim();
  const userId = await getAuthenticatedUserId();

  await ensureMyProfile();

  const { data, error } = await supabase
    .from('profiles')
    .update({ display_name: trimmed || null })
    .eq('id', userId)
    .select('id, display_name, avatar_url, friend_code, created_at, updated_at')
    .single();

  if (error) {
    logger.error('MY_PROFILE_UPDATE_ERROR', {
      message: error.message,
    });
    throw new Error(error.message);
  }

  const profile = parseMyProfile(data);
  persistCachedMyProfile(profile);
  return profile;
}
